/**
 * 材料種別マスタからフィルタ・登録モーダル用の選択肢を構築するフック
 *
 * 設計書 05-frontend-design.md §5.1 整合。
 * `PurchaseOrderListPage` のマスタ取得責務を分離し、ページコンポーネントを
 * レンダリング/モーダル制御に専念させる。
 */
import { useEffect, useState } from 'react';
import { logger } from '@/shared/lib/logger';
import { getMaterialTypes } from '@/features/inventory/services/inventoryApiService';
import type { MaterialTypeOption } from '../types/purchaseOrder';

interface UseMaterialTypeOptionsResult {
  readonly materialTypeOptions: readonly MaterialTypeOption[];
  /** 取得失敗時のエラー(成功時は null)。`usePurchaseOrders.error` と並列に Alert 表示する */
  readonly error: Error | null;
}

/**
 * 有効な材料種別のみを {value, label} 形式で返す。
 * fetch 失敗時はエラーを公開して呼出側で表示制御可能にする。
 */
export function useMaterialTypeOptions(): UseMaterialTypeOptionsResult {
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    readonly MaterialTypeOption[]
  >([]);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchAndBuildOptions = async (): Promise<void> => {
      try {
        const materialTypes = await getMaterialTypes();
        if (!isMounted) {
          return;
        }
        const options: readonly MaterialTypeOption[] = materialTypes
          .filter((type) => type.isActive)
          .map((type) => ({ value: type.id, label: type.displayName }));
        setMaterialTypeOptions(options);
      } catch (caughtError) {
        if (!isMounted) {
          return;
        }
        const errorInstance =
          caughtError instanceof Error
            ? caughtError
            : new Error('材料種別の取得に失敗しました');
        setError(errorInstance);
        logger.captureError(caughtError, 'useMaterialTypeOptions.fetchAndBuildOptions');
      }
    };
    void fetchAndBuildOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  return { materialTypeOptions, error };
}
